import { useMemo, useCallback } from "react";
import { z } from "zod";
import { useLingui } from "@lingui/react/macro";
import { BaseEditableForm } from "@/components/form/BaseEditableForm";
import type { FieldConfig, GroupConfig } from "@/components/form/types";
import { useUpdateSettlingIn } from "@/hooks/useSettlingIn";
import { useGroups } from "@/hooks/useGroups";
import { useChildcares } from "@/hooks/settings/useChildcare";
import type { SettlingInPlanDetail } from "@/types/settlingIn";
import { format } from "date-fns";
import { toOrgDateOnly } from "@/utils/dateUtils";
import { typeError } from "@/lib/zodTypeError";

interface OverviewFormData extends Record<string, unknown> {
  start_date: string;
  end_date: string;
  group_id: string;
  childcare_id: string;
  date_agreed: boolean;
  info_sent: boolean;
  info_confirmed: boolean;
  /**
   * UI-only (ST-5/S17): the three process flags rendered as ONE checklist
   * control. Mapped in from the three booleans in `defaultValues` and back out
   * to them in `handleSubmit`, so the persisted field names, values and payload
   * shape are unchanged — this is a rendering change only.
   */
  process_flags: Record<string, boolean>;
  comment: string;
}


interface OverviewTabProps {
  plan: SettlingInPlanDetail;
  planId: string;
}

export function OverviewTab({ plan, planId }: OverviewTabProps) {
  const { t } = useLingui();
  const updateMutation = useUpdateSettlingIn();
  const { data: groupsData } = useGroups(200, 0);
  const { data: childcaresData } = useChildcares({ limit: 200 });

  const groupOptions = useMemo(
    () => {
      const groups = Array.isArray(groupsData) ? groupsData : [];
      return groups.map((g) => ({ value: g.id || "", label: g.name || "" }));
    },
    [groupsData],
  );

  const childcareOptions = useMemo(
    () => {
      const childcares = Array.isArray(childcaresData)
        ? childcaresData
        : (childcaresData as { data?: { id?: string; name?: string }[] })?.data ||
          (childcaresData as { childcares?: { id?: string; name?: string }[] })?.childcares || [];
      return (childcares as { id?: string; name?: string }[]).map((cc) => ({ value: cc.id || "", label: cc.name || "" }));
    },
    [childcaresData],
  );

  const startDateRequiredMsg = t({ id: "settlingIn.validation.startDateRequired", message: "Start date is required" });
  const dateToString = (val: string | Date) => {
    if (val instanceof Date) return format(val, "yyyy-MM-dd");
    return val;
  };

  const schema = useMemo(() => z.object({
    start_date: z.union([
      z.string().min(1, startDateRequiredMsg),
      z.date({ error: typeError({ required: startDateRequiredMsg }) }),
    ]).transform(dateToString),
    end_date: z.union([
      z.string(),
      z.date(),
    ]).optional().default("").transform((val) => {
      if (!val) return "";
      return dateToString(val);
    }),
    group_id: z.string().min(1, t({ id: "settlingIn.validation.groupRequired", message: "Group is required" })),
    childcare_id: z.string().min(1, t({ id: "settlingIn.validation.childcareRequired", message: "Childcare is required" })),
    date_agreed: z.boolean().optional().default(false),
    info_sent: z.boolean().optional().default(false),
    info_confirmed: z.boolean().optional().default(false),
    // The UI-only checklist record. The three booleans above remain the source
    // of truth for the wire payload and are NOT collapsed into this object.
    process_flags: z.record(z.string(), z.boolean()).optional().default({}),
    comment: z.string().max(10000).optional().default(""),
    // `z.record(z.string(), z.boolean())` widens process_flags' index signature, so the
    // inferred output no longer matches OverviewFormData exactly. The runtime
    // shape is identical; one cast at the schema boundary keeps the form typed.
  }) as unknown as z.ZodType<OverviewFormData, OverviewFormData>, [t, startDateRequiredMsg]);

  const fields = useMemo<FieldConfig<OverviewFormData>[]>(() => [
    { name: "start_date", type: "date", label: t({ id: "settlingIn.fields.startDate", message: "Start Date" }), required: true },
    { name: "end_date", type: "date", label: t({ id: "settlingIn.fields.endDate", message: "End Date" }) },
    { name: "childcare_id", type: "select", label: t({ id: "settlingIn.fields.childcare", message: "Childcare" }), required: true, selectOptions: childcareOptions },
    { name: "group_id", type: "select", label: t({ id: "settlingIn.fields.group", message: "Group" }), required: true, selectOptions: groupOptions },
    // ST-5/S17: one compact checklist instead of three side-by-side Yes/No
    // pairs. Same three captions, same persisted field names and values.
    {
      name: "process_flags",
      type: "checklist",
      // Distinct from the enclosing group title ("Process Tracking") so the
      // heading is not announced twice.
      label: t({ id: "settlingIn.fields.processFlags", message: "Process steps" }),
      colSpan: 2,
      checklistOptions: [
        { value: "date_agreed", label: t({ id: "settlingIn.fields.dateAgreed", message: "Date Agreed" }), description: t({ id: "settlingIn.fields.dateAgreedDesc", message: "Parents confirmed start date" }) },
        { value: "info_sent", label: t({ id: "settlingIn.fields.infoSent", message: "Info Sent" }), description: t({ id: "settlingIn.fields.infoSentDesc", message: "Onboarding docs sent to parents" }) },
        { value: "info_confirmed", label: t({ id: "settlingIn.fields.infoConfirmed", message: "Info Confirmed" }), description: t({ id: "settlingIn.fields.infoConfirmedDesc", message: "Parents returned completed forms" }) },
      ],
    },
    { name: "comment", type: "textarea", label: t({ id: "settlingIn.fields.comment", message: "Comment" }) },
  ], [t, groupOptions, childcareOptions]);

  const groupConfig = useMemo<GroupConfig<OverviewFormData>[]>(() => [
    { title: t({ id: "settlingIn.groups.datesSchedule", message: "Dates & Schedule" }), fields: ["start_date", "end_date", "childcare_id", "group_id"] },
    { title: t({ id: "settlingIn.groups.processTracking", message: "Process Tracking" }), fields: ["process_flags"] },
    { title: t({ id: "settlingIn.groups.notes", message: "Notes" }), fields: ["comment"] },
  ], [t]);

  const defaultValues = useMemo<Partial<OverviewFormData>>(() => ({
    start_date: plan.start_date || "",
    end_date: plan.end_date || "",
    group_id: plan.group_id || "",
    childcare_id: plan.childcare_id || "",
    date_agreed: plan.date_agreed ?? false,
    info_sent: plan.info_sent ?? false,
    info_confirmed: plan.info_confirmed ?? false,
    // Map the three persisted booleans INTO the checklist control's record.
    process_flags: {
      date_agreed: plan.date_agreed ?? false,
      info_sent: plan.info_sent ?? false,
      info_confirmed: plan.info_confirmed ?? false,
    },
    comment: plan.comment || "",
  }), [plan]);

  // Format a date value (Date object or ISO string) to YYYY-MM-DD for the API
  const toApiDate = (val: string | Date | undefined | null): string | undefined => {
    if (!val) return undefined;
    if (val instanceof Date) return format(val, "yyyy-MM-dd");
    // `toOrgDateOnly` passes a bare "yyyy-MM-dd" through untouched. Routing it
    // through `new Date()` instead would read it as UTC midnight and post the
    // PREVIOUS day from any browser at a negative UTC offset — on every save,
    // cumulatively, for the plan's own anchor date.
    return toOrgDateOnly(val);
  };

  const handleSubmit = useCallback(async (values: OverviewFormData) => {
    // The checklist is the control the operator actually edits; fall back to the
    // flat booleans so a submit that never touched the control still persists
    // the seeded values rather than silently clearing them.
    const record = values.process_flags ?? {};
    const flags = {
      date_agreed: record.date_agreed ?? values.date_agreed ?? false,
      info_sent: record.info_sent ?? values.info_sent ?? false,
      info_confirmed: record.info_confirmed ?? values.info_confirmed ?? false,
    };

    await updateMutation.mutateAsync({
      id: planId,
      data: {
        start_date: toApiDate(values.start_date as unknown as string | Date) || "",
        end_date: toApiDate(values.end_date as unknown as string | Date),
        group_id: values.group_id,
        childcare_id: values.childcare_id,
        // Map the checklist record back OUT to the three persisted booleans, so
        // the payload shape (and `version`) is exactly what it was pre-S17.
        date_agreed: flags.date_agreed,
        info_sent: flags.info_sent,
        info_confirmed: flags.info_confirmed,
        comment: values.comment || undefined,
        version: plan.version || 0,
      },
    });
  }, [updateMutation, planId, plan.version]);

  return (
    <div className="space-y-6">
      <BaseEditableForm<OverviewFormData>
        schema={schema}
        fields={fields}
        groups={groupConfig}
        defaultValues={defaultValues}
        mode="edit"
        onSubmit={handleSubmit}
        isSubmitting={updateMutation.isPending}
      />
    </div>
  );
}
